import { useState } from 'react'
import {
  VStack,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  Menu,
  MenuButton,
  IconButton,
  MenuList,
  MenuItem
} from '@chakra-ui/react'
import NavBar from 'components/NavBar'
import { useSelector } from 'model/hooks'
import {
  ICourse,
  IEntityType,
  ILesson,
  ILessonType,
  ISection,
  SectionType
} from 'shared/types'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import get from 'lodash/get'
import Loading from 'components/Loading'
import LessonsList from 'components/LessonsList'
import {
  dbDeleteCourse,
  dbDuplicateCourse,
  dbDuplicateCourseFree,
  dbDuplicateCourseLibrary,
  dbUpdateCourse
} from 'controllers/courses'
import { useContext, useMemo, useRef } from 'react'
import LessonsContext from 'contexts/LessonsContext'
import { generateId } from 'controllers/db'
import { dbCreateLesson, dbDeleteLessonEntity } from 'controllers/lessons'
import filter from 'lodash/filter'
import DeleteConfirmation, {
  IDeleteConfirmationModal
} from 'modals/DeleteConfirmation'
import CustomTabPanel from 'components/CustomTabPanel'
import EntityUsers from 'components/EntityUsers'
import EntityContent from 'components/EntityContent'
import { ReactComponent as IconDots } from 'shared/assets/bsDotsVertical.svg'
import { ReactComponent as IconDelete } from 'shared/assets/bsTrash.svg'
import { ReactComponent as IconCopy } from 'shared/assets/bsDuplicate.svg'
import CoursePage from 'shared/pages/CoursePage'
import { storageRef } from 'controllers/db'
import { createSection } from 'controllers/sections'
import { arrayInsertAt, arrayMove } from 'shared/utils/array'
import EditSectionDrawer from 'modals/EditSectionDrawer'
import CourseSettingsDrawer, {
  ICourseSettingsDrawer
} from 'modals/course/CourseSettingsDrawer'
import Footer from 'shared/components/Footer'

const Course = () => {
  const navigate = useNavigate()
  const { lessons } = useContext(LessonsContext)
  const [searchParams, setSearchParams] = useSearchParams()
  const { entityId } = useParams() as { entityId: string }
  const deleteModalRef = useRef<IDeleteConfirmationModal>(null)
  const teachers = useSelector(state => state.teachers)
  const c: ICourse | undefined = useSelector(state =>
    get(state.courses, entityId)
  )
  const settingsDrawerRef = useRef<ICourseSettingsDrawer>(null)
  const [editSectionId, setEditSectionId] = useState<string>()
  const partitions = useSelector(state => state.partitions)

  const currentTabIndex = useMemo(() => {
    const tab = searchParams.get('tab')
    if (tab) {
      return +tab
    } else {
      return 0
    }
  }, [searchParams])

  const updateLessons = (ids: string[]) => {
    dbUpdateCourse(entityId, { lessons: ids })
  }

  const onDuplicateLesson = (lessonId: string) => {
    const l = lessons && lessons[lessonId]
    if (l && c) {
      const id = generateId()
      const newL: ILesson = {
        ...l,
        id,
        name: l.name + ' (copy)',
        entityId: c.id
      }
      dbCreateLesson(id, newL)
      dbUpdateCourse(entityId, { lessons: [...(c?.lessons || []), id] })
    }
  }

  const onAddLesson = () => {
    if (c) {
      const id = generateId()
      const newL: ILesson = {
        id,
        name: 'Новый урок',
        entityId: c.id,
        type: ILessonType.LECTURE
      }
      dbCreateLesson(id, newL)
      dbUpdateCourse(entityId, { lessons: [...(c?.lessons || []), id] })
    }
  }

  const onDeleteLesson = (lessonId: string) => {
    if (c) {
      const newIds = filter(c.lessons, lId => lId !== lessonId)
      dbUpdateCourse(entityId, { lessons: newIds })
      dbDeleteLessonEntity(lessonId, c.id)
    }
  }

  const onLessonClick = (id: string) => {
    navigate(`/courses/${entityId}/${id}`)
  }

  const onDelete = () => {
    console.log('deleteCourse')
    if (c) {
      dbDeleteCourse(c)
      navigate('/courses')
    }
  }

  const onDuplicate = async () => {
    if (c && lessons) {
      const newCourseId = await dbDuplicateCourse(c, lessons)
      navigate(`/courses/${newCourseId}`)
    }
  }

  const onDuplicateFree = async () => {
    if (c && lessons) {
      const newCourseId = await dbDuplicateCourseFree(c, lessons)
      navigate(`/free/${newCourseId}`)
    }
  }

  const onDuplicateLibrary = async () => {
    if (c && lessons) {
      const newCourseId = await dbDuplicateCourseLibrary(c, lessons)
      navigate(`/library/${newCourseId}`)
    }
  }

  const onAddSection = (sType: SectionType, i: number) => {
    if (c) {
      const s = createSection(sType)
      const newSections = {
        ...(c.sections || {}),
        [s.id]: s
      }
      const newSectionsOrder = arrayInsertAt(c.sectionsOrder || [], i, s.id)
      const upd = {
        sections: newSections,
        sectionsOrder: newSectionsOrder
      }
      dbUpdateCourse(c.id, upd)
      setEditSectionId(s.id)
    }
  }

  const onMoveSection = (fromIndex: number, toIndex: number) => {
    console.log('onMoveSection', fromIndex, toIndex)
    if (c && c.sectionsOrder) {
      const newSectionsOrder = arrayMove(c.sectionsOrder, fromIndex, toIndex)
      const upd = {
        sectionsOrder: newSectionsOrder
      }
      dbUpdateCourse(c.id, upd)
    }
  }

  const updateSection = (s: ISection) => {
    if (c) {
      const upd = {
        sections: {
          ...(c.sections || {}),
          [s.id]: s
        }
      }
      dbUpdateCourse(c.id, upd)
    }
  }

  const onEditSection = (sId: string) => {
    setEditSectionId(sId)
  }

  const onDeleteSection = () => {
    if (c && editSectionId) {
      const newOrder = filter(c.sectionsOrder, lId => lId !== editSectionId)
      const newSections = { ...c.sections }
      delete newSections[editSectionId]
      setEditSectionId(undefined)
      const upd = { sections: newSections, sectionsOrder: newOrder }
      dbUpdateCourse(c.id, upd)
    }
  }

  const onDuplicateSection = () => {
    if (c && editSectionId && c.sections && c.sectionsOrder) {
      const s = c.sections[editSectionId]
      const index = c.sectionsOrder.indexOf(editSectionId)
      const id = generateId()
      const newS: ISection = {
        ...s,
        id
      }
      const newSections = {
        ...c.sections,
        [newS.id]: newS
      }
      const newSectionsOrder = arrayInsertAt(
        c.sectionsOrder || [],
        index + 1,
        newS.id
      )
      const upd = { sections: newSections, sectionsOrder: newSectionsOrder }
      dbUpdateCourse(c.id, upd)
      setEditSectionId(id)
    }
  }

  const navBarActions = (
    <Menu>
      <MenuButton
        as={IconButton}
        icon={<IconDots />}
        colorScheme='wood'
        variant={'outline'}
      />
      <MenuList>
        <MenuItem
          icon={<IconCopy width={12} height={12} />}
          onClick={onDuplicate}
          textStyle={'small'}
        >
          Duplicate
        </MenuItem>
        <MenuItem
          icon={<IconCopy width={12} height={12} />}
          onClick={onDuplicateFree}
          textStyle={'small'}
        >
          Duplicate to free
        </MenuItem>
        <MenuItem
          icon={<IconCopy width={12} height={12} />}
          onClick={onDuplicateLibrary}
          textStyle={'small'}
        >
          Duplicate to library
        </MenuItem>
        <MenuItem
          color='red.800'
          icon={<IconDelete width={12} height={12} />}
          onClick={() => deleteModalRef.current?.open()}
          textStyle={'small'}
        >
          Delete
        </MenuItem>
      </MenuList>
    </Menu>
  )

  const renderTabs = () => {
    return (
      <TabList
        display='flex'
        alignItems='center'
        justifyContent='center'
        // borderBottom='none'
        p='4'
        overflow={'hidden'}
      >
        <Tab>Страница курса</Tab>
        <Tab>Уроки</Tab>
        <Tab>Ученики</Tab>
      </TabList>
    )
  }

  if (!c) {
    return <Loading />
  } else {
    return (
      <VStack
        w='full'
        h='full'
        spacing={0}
        overflow={'hidden'}
        maxH={'full'}
        sx={{ '--chakra-zIndices-modal': '2' }}
      >
        <Tabs
          size='sm'
          isLazy
          colorScheme='wood'
          variant='soft-rounded'
          w='full'
          overflow={'hidden'}
          h='full'
          flex={1}
          p={0}
          display={'flex'}
          flexDirection={'column'}
          index={currentTabIndex}
          onChange={(tabIndex: number) =>
            setSearchParams({ tab: tabIndex.toString() })
          }
        >
          <NavBar title={c.title} tabs={renderTabs()} backUrl='/courses'>
            {navBarActions}
          </NavBar>

          <TabPanels overflow={'hidden'} flex={1}>
            <CustomTabPanel bg='black.950'>
              <VStack
                w='full'
                bg={'wood.100'}
                borderBottomRadius={{ base: 'sm', lg: 'lg' }}
                overflowY={'auto'}
              >
                <VStack
                  w='full'
                  justify={'flex-start'}
                  flex={1}
                  spacing={0}
                  h='full'
                  overflowX={'hidden'}
                  overflowY={'auto'}
                  minH='full'
                  bg='black.950'
                >
                  <VStack
                    w='full'
                    bg={'wood.100'}
                    borderBottomRadius={{ base: 'sm', lg: 'lg' }}
                  >
                    <CoursePage
                      storageRef={storageRef}
                      course={c}
                      onPay={() => null}
                      toCourse={() => null}
                      userCourses={{}}
                      admin={{
                        onAddSection,
                        onMoveSection,
                        onEditSection,
                        onEditHeader: () =>
                          settingsDrawerRef.current?.open(c.id)
                      }}
                      user={null}
                      dbSaveFeedback={() => undefined}
                      teachers={teachers || {}}
                      partitions={partitions}
                    />
                  </VStack>
                  <Footer />
                </VStack>
              </VStack>
            </CustomTabPanel>
            <CustomTabPanel>
              {c.lessons && c.lessons.length > 0 ? (
                <LessonsList
                  ids={c.lessons}
                  updateIds={updateLessons}
                  onAddLesson={onAddLesson}
                  onDuplicate={onDuplicateLesson}
                  onDelete={onDeleteLesson}
                  onClick={onLessonClick}
                />
              ) : (
                <EntityContent
                  content={c.content || []}
                  entityType={IEntityType.COURSES}
                  entityId={c.id}
                />
              )}
            </CustomTabPanel>
            <CustomTabPanel>
              <EntityUsers />
            </CustomTabPanel>
          </TabPanels>
        </Tabs>
        <DeleteConfirmation
          ref={deleteModalRef}
          title='Удаление курса'
          description={`Вы собираетесь удалить курс "${c.title}" и все его уроки. Курс станет не доступен для пользователей купивших его.`}
          onDelete={onDelete}
        />
        <CourseSettingsDrawer ref={settingsDrawerRef} />
        <EditSectionDrawer
          s={editSectionId && get(c, ['sections', editSectionId])}
          onChange={updateSection}
          onClose={() => setEditSectionId(undefined)}
          entityId={c.id}
          onDelete={onDeleteSection}
          onDuplicate={onDuplicateSection}
        />
      </VStack>
    )
  }
}

export default Course
